import { Alert, Box, Button, MenuItem, Typography } from '@mui/material';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { RHFSelect, RHFTextField } from 'src/components/hook-form';
import { translateKey } from 'src/locales/translate';
import {
  useStockBatches,
  useStockProduct,
  useStockProductLoading,
  useWarehouseStockEnabled,
} from 'src/sections/@dashboard/warehouse/hooks/useClinicalStockProducts';
import { TextSkeleton } from 'src/sections/@dashboard/warehouse/components/WarehouseDetailDialogSkeleton';
import {
  formatMilli,
  formatProductStock,
  formatUnits,
  todayDateOnly,
  unitLabelsFor,
} from 'src/sections/@dashboard/warehouse/utils/clinicalStock';
import MedicationSectionDivider from './MedicationSectionDivider';
import MedicationStockBatchField from './MedicationStockBatchField';
import MedicationStockBatchFieldSkeleton from './MedicationStockBatchFieldSkeleton';
import MedicationStockHandoverFields from './MedicationStockHandoverFields';
import MedicationStockIssueNote from './MedicationStockIssueNote';
import MedicationStockOptedOut from './MedicationStockOptedOut';
import { SPAN_FULL } from './medicationFormLayout';
import { MedicationsFormModalValuesProps } from './medicationFormSchema';
import { openedTakingProductId } from './medicationStockValues';
import {
  StockSectionVariant,
  medicationStockView,
} from './medicationStockView';
import { selectTestId } from './selectTestId';

interface MedicationStockSectionProps {
  /** The host holds the form: an unconfirmed save or a save in flight. */
  disabled?: boolean;
  /** Phase 4 wires "Zgłoś zapotrzebowanie"; without it the button is hidden. */
  onRequestDemand?: (productId: string) => void;
  /** A treatment line or template shows less (F-7); default: a Medication. */
  variant?: StockSectionVariant;
  /** #688 R1-2: the owner's address already on screen, for a new Rx supply. */
  ownerAddress?: string;
}

/**
 * "Magazyn" (#692 rev B): the step before the summary, there once step 1
 * brought a Product — a Warehouse pick, or a saved taking. A new taking's
 * values are worked out from the dosing (`MedicationStockIssueDefault`,
 * which outlives the step) and labelled as assumptions while the vet can
 * change them, with a line explaining the quantity; the vet changes what
 * they disagree with and goes on. A saved taking is the vet's answer. "Nie
 * pobieraj z magazynu" collapses the step (for a saved taking that is what
 * reverses it on save, R12) and "Pobierz jednak" undoes that. While the
 * Warehouse is disabled (revision R1) a saved taking stays read-only and
 * the payload repeats it; an unsaved one can still be opted out, so a form
 * is never stuck sending stock it cannot post.
 */
export default function MedicationStockSection({
  disabled,
  onRequestDemand,
  variant = 'MEDICATION',
  ownerAddress,
}: MedicationStockSectionProps): React.ReactElement | null {
  const { watch, setValue, formState } =
    useFormContext<MedicationsFormModalValuesProps>();
  const [
    enabled,
    productId,
    quantity,
    measure,
    batchId,
    handover,
    wasSet,
    isRx,
  ] = watch([
    'stockEnabled',
    'stockProductId',
    'stockQuantity',
    'stockMeasure',
    'stockBatchId',
    'stockHandover',
    'stockWasSet',
    // P7B-R6: as the schema, the payload and the summary read it.
    'stockProductIsRx',
  ]);
  const warehouseEnabled = useWarehouseStockEnabled();
  const locked = Boolean(disabled) || !warehouseEnabled;
  const isTemplate = variant === 'TEMPLATE_LINE';
  const product = useStockProduct(enabled ? productId : '');
  const batches = useStockBatches(enabled ? productId : '');
  // The picked Product's first read: its stock line and batches hold their place.
  const productLoading = useStockProductLoading(enabled ? productId : '');
  const view = medicationStockView({
    product,
    batches,
    today: todayDateOnly(),
    productId,
    quantity,
    measure,
    batchId,
    saved: formState.defaultValues,
  });
  // `warehouse.units.<UNIT>` is a plural-forms object; Phase 1 picks the form.
  const inUnits = (milli: number): string =>
    product ? formatUnits(milli, unitLabelsFor(product)) : formatMilli(milli);

  if (!productId) return null;
  const disabledNotice = warehouseEnabled ? null : (
    <Alert
      severity="info"
      sx={SPAN_FULL}
      data-testid="medication-stock-disabled"
    >
      {translateKey('warehouse.clinical.disabledReadOnly')}
    </Alert>
  );
  // A session pick opted out after the Warehouse went off: nothing is taken
  // and nothing can be.
  if (!warehouseEnabled && !wasSet && !enabled) return disabledNotice;
  if (!enabled) {
    return (
      <>
        {disabledNotice}
        <MedicationStockOptedOut
          saved={wasSet}
          disabled={Boolean(disabled)}
          onTakeAgain={() =>
            setValue('stockEnabled', true, { shouldDirty: true })
          }
        />
      </>
    );
  }
  // Only a saved taking stays put while the Warehouse is disabled: its
  // reversal would be refused.
  const optOutLocked = Boolean(disabled) || (!warehouseEnabled && wasSet);
  const computed =
    !locked && productId !== openedTakingProductId(formState.defaultValues);

  return (
    <>
      {computed && (
        <MedicationSectionDivider
          label={translateKey('warehouse.clinical.assumptions')}
          testId="medication-stock-assumptions"
        />
      )}
      {disabledNotice}
      {product && (
        <Typography
          variant="subtitle2"
          color={product.onHandMilli <= 0 ? 'error' : 'text.primary'}
          sx={SPAN_FULL}
          data-testid="medication-stock-header"
        >
          {translateKey('warehouse.clinical.blockHeader', {
            product: product.name,
            stock: formatProductStock(product.onHandMilli, product),
          })}
        </Typography>
      )}
      {!product && productLoading && (
        <TextSkeleton
          variant="subtitle2"
          width={260}
          sx={SPAN_FULL}
          testId="medication-stock-header-skeleton"
        />
      )}
      {/* How it left comes first: handed over, a Product dispensed in
          packages counts "Ilość" in them. Its follow-up questions stay
          right under it. */}
      <MedicationStockHandoverFields
        locked={locked}
        handover={handover}
        isRx={isRx}
        isTemplate={isTemplate}
        prefill={!locked && !wasSet}
        ownerAddress={ownerAddress}
      />
      <RHFTextField
        name="stockQuantity"
        type="number"
        label={translateKey('warehouse.clinical.quantity')}
        disabled={locked}
        inputProps={{
          min: 0,
          step: 'any',
          'data-testid': 'medication-stock-quantity',
        }}
      />
      <RHFSelect
        name="stockMeasure"
        label={translateKey('warehouse.clinical.measure')}
        disabled={locked}
        SelectProps={{
          native: false,
          SelectDisplayProps: selectTestId('medication-stock-measure'),
        }}
      >
        <MenuItem value="UNIT">
          {translateKey('warehouse.clinical.measureUnit')}
        </MenuItem>
        <MenuItem value="PACKAGE">
          {translateKey('warehouse.clinical.measurePackage')}
        </MenuItem>
      </RHFSelect>
      {product && (
        <MedicationStockIssueNote product={product} locked={locked} />
      )}

      {variant === 'MEDICATION' && product && (
        <MedicationStockBatchField
          view={view}
          batchId={batchId}
          locked={locked}
          inUnits={inUnits}
        />
      )}
      {variant === 'MEDICATION' && !product && productLoading && (
        <MedicationStockBatchFieldSkeleton />
      )}

      {view.shortage && !isTemplate && (
        <Alert
          severity="warning"
          sx={SPAN_FULL}
          data-testid="medication-stock-shortage"
          action={
            // D-1: with the Warehouse off there is nothing to request.
            onRequestDemand && warehouseEnabled ? (
              <Button
                color="inherit"
                size="small"
                data-testid="medication-stock-request-demand"
                onClick={() => onRequestDemand(productId)}
              >
                {translateKey('warehouse.clinical.requestDemand')}
              </Button>
            ) : undefined
          }
        >
          {translateKey('warehouse.clinical.shortage', {
            available: inUnits(view.availableMilli),
            requested: inUnits(view.requestedMilli),
          })}
        </Alert>
      )}

      <Box sx={SPAN_FULL}>
        <Button
          size="small"
          color="inherit"
          disabled={optOutLocked}
          onClick={() => setValue('stockEnabled', false, { shouldDirty: true })}
          data-testid="medication-stock-opt-out"
        >
          {translateKey('warehouse.clinical.optOut')}
        </Button>
      </Box>
    </>
  );
}
